import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import {
  catchError,
  concatMap,
  finalize,
  mergeMap
} from 'rxjs/operators';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { FirebaseRefreshTokenResponse } from '../models/firebase';
import { EventsService } from '../services/events.service';
import { StorageKeyEnum } from '../enums/storage';
import { IframeEventsService } from '../services/iframe-events.service';
import { LoginResult } from '../models/auth';
import { environment } from 'src/environments/environment';

export const USE_OLD_ID_TOKEN_INTERCEPTOR = new HttpContextToken<boolean>(() => false);
export const USE_API_KEY = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private loadingCounter = 0;
  private isRefreshing = false;
  private refreshToken$ = new Subject();

  constructor(
    private authService: AuthService,
    private loadingSpinnerService: LoadingSpinnerService,
    private eventService: EventsService,
    private iFrameEventService: IframeEventsService,
  ) { }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const authToken = this.authService.getAuthToken();
    const idToken = this.authService.getV2AuthToken();

    const excludeLoadingSpinner = this.loadingSpinnerService
      .getExcludeLoadingSpinner$()
      .getValue();

    if (!excludeLoadingSpinner) {
      this.showLoadingSpinner();
      this.loadingCounter++;
    }

    // old api calls to v2 need to use an id token instead of and acm token
    if (req.context.get(USE_OLD_ID_TOKEN_INTERCEPTOR) && idToken) {
      req = req.clone({
        setHeaders: {
          Authorization: `Bearer ${idToken}`
        }
      });
      return this.handleRequest(req, next, excludeLoadingSpinner);
    }

      // Use api key for a call
      // hard coded for now
    if (req.context.get(USE_API_KEY) && idToken) {
      req = req.clone({
        setHeaders: {
          'api-key': environment.picupV2.apiKey
        }
      });
      return this.handleRequest(req, next, excludeLoadingSpinner);
    }

    if (req.url.includes('cloudfunctions')) return this.handleRequest(req, next, excludeLoadingSpinner);

    // only if Iframed in
    if (!authToken) {
      //removed for now
      // return this.iFrameEventService.authDataReady.pipe(
      //   take(1),
      //   mergeMap(() => {
      //     const updatedAuthToken = this.authService.getAuthToken();
      //     const idToken = this.authService.getV2AuthToken();

      //     if (!req.url.includes('/token?key=')) {
      //       req = req.clone({
      //         setHeaders: { Authorization: `Bearer ${req.context.get(USE_OLD_ID_TOKEN_INTERCEPTOR) ? idToken : updatedAuthToken}` },
      //       });
      //     }
      //     return this.handleRequest(req, next, excludeLoadingSpinner);
      //   }),
      // );

      return this.handleRequest(req, next, excludeLoadingSpinner);
    } else {
      if (!req.url.includes('/token?key=')) {
        req = req.clone({
          setHeaders: { Authorization: `Bearer ${authToken}` },
        });
      }

      return this.handleRequest(req, next, excludeLoadingSpinner);
    }
  }

  private handleRequest(
    req: HttpRequest<any>,
    next: HttpHandler,
    excludeLoadingSpinner: boolean,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      finalize(() => {

        if (excludeLoadingSpinner) {
          return;
        }
        this.loadingCounter--;

        if (this.loadingCounter === 0) {
          this.hideLoadingSpinner();
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      if (this.authService.getAuthToken()) {
        const firebaseRefreshToken = this.authService.getFirebaseRefreshToken();

        if (!firebaseRefreshToken) {
          return throwError(
            () => new Error('handle401Error - Firebase Refresh Token is null'),
          );
        }

        return this.authService.refreshToken(firebaseRefreshToken).pipe(
          mergeMap((response: FirebaseRefreshTokenResponse) => {
            return this.authService
              .loginAccessManagement(response.id_token)
              .pipe(
                concatMap((loginResponse: LoginResult) => {
                  // Wait for the token refresh to complete
                  this.authService.updateToken(
                    loginResponse.AccessToken,
                    firebaseRefreshToken,
                  );
                  localStorage.setItem(
                    StorageKeyEnum.AuthToken,
                    loginResponse.AccessToken,
                  );
                  this.eventService.notifyParentOfTokenRefresh({
                    ...loginResponse,
                    FirebaseRefreshToken: firebaseRefreshToken,
                    AuthToken: ''
                  });
                  this.isRefreshing = false;
                  this.refreshToken$.next(null); // Emit a value to trigger the original request
                  return next.handle(
                    this.updateRequestWithNewToken(
                      req,
                      loginResponse.AccessToken,
                    ),
                  );
                }),
              );
          }),
          catchError((error: HttpErrorResponse) => {
            this.isRefreshing = false;
            if (error.status === 403) {
              // TODO: Figure out how to direct the user to the auth service here...
            }
            return throwError(() => error);
          }),
        );
      }
    } else {
      // If the token is already refreshing, queue the original request until the token is refreshed
      return this.refreshToken$.pipe(concatMap(() => next.handle(req)));
    }

    return throwError(() => new Error('handle401Error - Unexpected state'));
  }

  private updateRequestWithNewToken(
    request: HttpRequest<any>,
    newToken: string,
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: { Authorization: `Bearer ${newToken}` },
    });
  }

  private showLoadingSpinner(): void {
    if (this.loadingCounter === 0) {
      this.loadingSpinnerService.show();
    }
  }

  private hideLoadingSpinner(): void {
    if (this.loadingCounter === 0) {
      this.loadingSpinnerService.hide();
    }
  }
}
